import React from 'react';
import { Control, useWatch } from 'react-hook-form';
import { FieldError } from 'react-hook-form';
import { SearchableSelect } from '../Form/SearchableSelect';
import { useQuery } from 'react-query';
import { listEventsFilter, findEventById } from '../../api/events';
import { useAuth } from '../../contexts/Auth';
import { useDebounce } from 'use-debounce';
import { EventResponseListElement } from '../../interfaces/events';

interface EventSearchSelectProps {
  name: string;
  control?: Control<any>;
  label: string;
  error?: FieldError;
  defaultValue?: number;
  required?: boolean;
  sectionId?: number;
  onEventSelect?: (event: EventResponseListElement) => void;
}

const EventSearchSelect: React.FC<EventSearchSelectProps> = ({
  name,
  label,
  control,
  error,
  defaultValue,
  required = false,
  sectionId,
  onEventSelect,
}) => {
  const [{ token }] = useAuth();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery] = useDebounce(searchQuery, 300);
  const value = useWatch({ control, name }) as number;

  const query = useQuery(
    ['events-select', debouncedQuery, value, sectionId],
    async (): Promise<{ data: EventResponseListElement[] }> => {
      const selected = value
        ? await findEventById(value.toString(), token)
        : null;
      const events = await listEventsFilter(
        {
          search: searchQuery,
          pageSize: 20,
          page: 1,
        },
        token,
      );

      if (!selected || !selected?.data?.data) return events;

      const selectedEvent = selected.data.data;
      return {
        ...events,
        data: [
          selectedEvent,
          ...events.data.filter((e) => e.id !== selectedEvent.id),
        ],
      };
    },
  );

  const options = React.useMemo(() => {
    if (!query.data) return [];
    const filteredEvents = sectionId
      ? query.data.data.filter(
          (event) => event.attributes.section?.data?.id === sectionId,
        )
      : query.data.data;

    return filteredEvents.map((event) => ({
      value: event.id,
      label: `${event.attributes.title} (${event.attributes.section?.data?.attributes?.name || 'N/A'})`,
      data: event,
    }));
  }, [query.data, sectionId]);

  const handleChange = (
    selectedOption: { data: EventResponseListElement } | null,
  ) => {
    if (onEventSelect && selectedOption?.data) {
      onEventSelect(selectedOption.data);
    }
  };

  const handleInput = (input: string) => {
    setSearchQuery(input);
  };

  return (
    <SearchableSelect
      name={name}
      label={label}
      control={control}
      error={error}
      options={options}
      onInput={handleInput}
      isLoading={query.isFetching}
      onChange={handleChange}
      registerOptions={{
        setValueAs: (value) => {
          return value?.value;
        },
      }}
    />
  );
};

export default EventSearchSelect;
